import { useState, useEffect } from "react";
import { Award, Sparkles, Download, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function Certificates() {
    const [interns, setInterns] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [drafting, setDrafting] = useState(false);

    // Form states
    const [recipientType, setRecipientType] = useState("intern"); // "intern" | "employee"
    const [selectedInternId, setSelectedInternId] = useState("");
    const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
    const [content, setContent] = useState("");

    // Load templates/interns/documents
    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoading(true);
        try {
            const [internsRes, empsRes, docsRes] = await Promise.all([
                api.get("/interns"),
                api.get("/employees"),
                api.get("/documents?documentType=certificate")
            ]);
            setInterns(internsRes.data.items || []);
            setEmployees(empsRes.data.items || []);
            setDocuments(docsRes.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handleRecipientTypeChange = (val) => {
        setRecipientType(val);
        setSelectedInternId("");
        setSelectedEmployeeId("");
        setContent("");
    };

    const handleAIDraft = async () => {
        let name = "";
        let role = "";
        let duration = "3 months";

        if (recipientType === "intern") {
            if (!selectedInternId) {
                toast.error("Please select an intern first");
                return;
            }
            const intern = interns.find((i) => i.id === selectedInternId);
            if (!intern) return;
            name = intern.fullName;
            role = intern.role;
            const start = intern.startDate || "";
            const end = intern.endDate || "";
            duration = start && end ? `${start} to ${end}` : "3 months";
        } else {
            if (!selectedEmployeeId) {
                toast.error("Please select an employee first");
                return;
            }
            const emp = employees.find((e) => e.id === selectedEmployeeId);
            if (!emp) return;
            name = emp.name;
            role = emp.designation || emp.role || "Employee";
            duration = emp.joining_date ? `since ${emp.joining_date}` : "employment period";
        }

        setDrafting(true);
        try {
            const r = await api.post("/documents/ai/draft-completion", {
                name,
                role,
                duration
            });
            setContent(r.data.content);
            toast.success("AI draft generated successfully!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDrafting(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        
        if (recipientType === "intern" && !selectedInternId) {
            toast.error("Please select an intern");
            return;
        }
        if (recipientType === "employee" && !selectedEmployeeId) {
            toast.error("Please select an employee");
            return;
        }

        setGenerating(true);
        try {
            const payload = {
                content: content,
                custom_data: {}
            };

            if (recipientType === "intern") {
                const intern = interns.find((i) => i.id === selectedInternId);
                payload.internId = selectedInternId;
                payload.custom_data = {
                    name: intern.fullName,
                    role: intern.role,
                    startDate: intern.startDate,
                    endDate: intern.endDate
                };
            } else {
                const emp = employees.find((e) => e.id === selectedEmployeeId);
                payload.employeeId = selectedEmployeeId;
                payload.custom_data = {
                    name: emp.name,
                    role: emp.designation || emp.role || "Employee",
                    startDate: emp.joining_date,
                    endDate: ""
                };
            }

            await api.post("/documents/generate/certificate", payload);
            toast.success("Certificate generated successfully!");
            setSelectedInternId("");
            setSelectedEmployeeId("");
            setContent("");
            init();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    const handleRevoke = async (certId) => {
        if (!confirm(`Are you sure you want to revoke certificate ${certId}?`)) return;
        try {
            await api.post(`/documents/${certId}/revoke`);
            toast.success("Certificate revoked successfully");
            init();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDownload = (certId) => {
        window.open(`${api.defaults.baseURL}/verify/download/${certId}`, "_blank");
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100 font-heading">Internship Certificates</h1>
                <p className="text-sm text-muted-foreground">Generate, view, and manage QR-coded completion certificates.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Generation Form */}
                <div className="lg:col-span-1 glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm self-start">
                    <h3 className="font-heading font-bold text-slate-800 dark:text-slate-100 text-lg mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary" />
                        <span>Generate Certificate</span>
                    </h3>

                    <form onSubmit={handleGenerate} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Recipient Source</label>
                            <select
                                value={recipientType}
                                onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                            >
                                <option value="intern">Select Intern</option>
                                <option value="employee">Select Employee</option>
                            </select>
                        </div>

                        {recipientType === "intern" ? (
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Choose Intern</label>
                                <select
                                    required
                                    value={selectedInternId}
                                    onChange={(e) => setSelectedInternId(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                >
                                    <option value="">-- Choose Intern --</option>
                                    {interns.map((i) => (
                                        <option key={i.id} value={i.id}>
                                            {i.fullName} ({i.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Choose Employee</label>
                                <select
                                    required
                                    value={selectedEmployeeId}
                                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                >
                                    <option value="">-- Choose Employee --</option>
                                    {employees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.name} ({emp.designation || emp.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Certificate Body Text (Optional)</label>
                                <button
                                    type="button"
                                    onClick={handleAIDraft}
                                    disabled={drafting || (recipientType === "intern" ? !selectedInternId : !selectedEmployeeId)}
                                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-hover disabled:text-muted-foreground font-semibold"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span>{drafting ? "Drafting..." : "AI Assist"}</span>
                                </button>
                            </div>
                            <textarea
                                rows={5}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground leading-relaxed"
                                placeholder="Click AI Assist or enter custom completion description..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={generating}
                            className="w-full flex justify-center items-center gap-2 bg-primary hover:bg-primary-hover disabled:bg-primary/50 text-white py-2.5 rounded-xl text-sm font-semibold shadow-md transition"
                        >
                            {generating ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Award className="w-4 h-4" />
                            )}
                            <span>Generate Certificate</span>
                        </button>
                    </form>
                </div>

                {/* Generated History List */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="glass p-4 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm flex items-center justify-between">
                        <span className="font-heading font-semibold text-slate-800 dark:text-slate-100 text-sm">Issued Certificates ({documents.length})</span>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="glass py-16 text-center rounded-2xl border border-slate-200 dark:border-white/10">
                            <p className="text-muted-foreground text-sm">No certificates issued yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {documents.map((doc) => (
                                <div key={doc.id} className="glass p-5 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="font-mono text-xs text-primary font-bold">{doc.certificateId}</span>
                                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase
                                                ${doc.status === "active" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" : "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400"}`}
                                            >
                                                {doc.status}
                                            </span>
                                        </div>

                                        <h4 className="font-heading font-bold text-slate-800 dark:text-slate-100 text-base">{doc.custom_data?.name}</h4>
                                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 font-medium">
                                            {doc.custom_data?.role}
                                            {doc.custom_data?.startDate && ` — ${doc.custom_data.startDate}`}
                                            {doc.custom_data?.endDate && ` to ${doc.custom_data.endDate}`}
                                        </p>
                                        
                                        {doc.content && (
                                            <p className="text-[11px] text-muted-foreground line-clamp-2 mt-3 bg-slate-50 dark:bg-slate-900 p-2.5 rounded border border-slate-100 dark:border-white/5 font-mono leading-relaxed">
                                                {doc.content}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-2 border-t border-slate-200 dark:border-white/10 pt-4 mt-5">
                                        <button
                                            onClick={() => handleDownload(doc.certificateId)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-lg text-slate-700 dark:text-slate-200 transition"
                                        >
                                            <Download className="w-3.5 h-3.5" />
                                            <span>Download</span>
                                        </button>
                                        {doc.status === "active" && (
                                            <button
                                                onClick={() => handleRevoke(doc.certificateId)}
                                                className="px-3 py-2 text-xs font-semibold bg-rose-50 hover:bg-rose-100 rounded-lg text-rose-700 transition"
                                                title="Revoke Certificate"
                                            >
                                                <ShieldAlert className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
